-- | Step 3 signature group for a document.
-- |
-- | Renders all signature fields and returns signature requests to the app
-- | runtime.
module Widgets.Signature.Group where

import Common

import API.Account (AccountInfo)
import API.Signature (BaseSigDefaults, SigVerification, SignResult, SignatureRequest)
import API.Types (DocumentID)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Array (catMaybes, filter, mapWithIndex)
import Data.Array as Arr
import Internal.Util (justIf)
import Widgets.Modal (ModalResult(..), modalWidget)
import Widgets.Signature (LoadDefaultsOp(..), SigWidgetMutation, SigWidgetResult(..), SigWidgetState, loadDefaults, loadSignature, loadVerification, signatureWidget)
import Widgets.Signature.Email (EmailListEntry, emailAllForm)
import Widgets.Signature.Types (DocSigField, SignKey, normalizeDocSigField)

type SigGroupState =
    { id :: String
    , docId :: DocumentID
    , sigs :: Array SigWidgetState
    , account :: AccountInfo
    }

data SigGroupAction
    = EmailAll

data SigGroupRequest
    = SignatureWidgetRequest SigWidgetMutation
    | SignatureEmailRequests (Array SignatureRequest)

data SigGroupResult
    = SigGroupChanged SigGroupState
    | SigGroupMutation SigGroupState SigGroupRequest

initSigGroup :: AccountInfo -> String -> DocumentID -> Array SigWidgetState -> SigGroupState
initSigGroup account id docId sigs = { id, docId, sigs, account }

type SigsLoadable defs r =
    { signatures :: Array SignResult
    , verifications :: Array SigVerification
    , signature_fields :: Array (BaseSigDefaults defs)
    | r
    }

loadVerifications :: Array SigVerification -> SigGroupState -> SigGroupState
loadVerifications verifications =
    loadSignatures { signatures: [], signature_fields: [], verifications }

loadSignatures :: forall defs r. SigsLoadable defs r -> SigGroupState -> SigGroupState
loadSignatures { signatures, verifications, signature_fields } state =
    state { sigs = map loadSig state.sigs }
  where
    --
    findKey :: forall s. Array { key :: SignKey | s } -> SignKey -> Maybe { key :: SignKey | s }
    findKey arr key = find (\r -> r.key == key) arr

    findVerif :: SignKey -> Maybe SigVerification
    findVerif key = find (\r -> r.data.key == key) verifications

    loadSig :: SigWidgetState -> SigWidgetState
    loadSig ssig = case findKey signatures ssig.field.key of
        Just sig -> loadSignature ssig sig
        Nothing -> loadVerif ssig

    loadVerif :: SigWidgetState -> SigWidgetState
    loadVerif ssig = case findVerif ssig.field.key of
        Just verif -> loadVerification ssig verif
        Nothing -> loadDefs ssig

    loadDefs :: SigWidgetState -> SigWidgetState
    loadDefs ssig = case findKey signature_fields ssig.field.key of
        Just verif -> loadDefaults AuthoritativeOp ssig verif
        Nothing -> ssig

-- | Update default signatures in signature state during autofill.
updateDefSignatures :: forall defs. SigGroupState -> Array (BaseSigDefaults defs) -> SigGroupState
updateDefSignatures sgs defs = sgs {sigs = map updateSig sgs.sigs }
  where
    findKey :: forall r. Array { key :: SignKey | r } -> SignKey -> Maybe { key :: SignKey | r }
    findKey arr key = find (\r -> r.key == key) arr

    updateSig :: SigWidgetState -> SigWidgetState
    updateSig ssig = case findKey defs ssig.field.key of
        Just def -> loadDefaults AutofillOp ssig def
        Nothing -> ssig

-- | A widget which displays a group of signature elements and provides a modal
-- | to email all of them.
signatureGroup :: SigGroupState -> Widget HTML SigGroupResult
signatureGroup state = renderSigGroup state >>= case _ of
    Left result -> pure result
    Right act -> handleSGAction state act

handleSGAction :: SigGroupState -> SigGroupAction -> Widget HTML SigGroupResult
handleSGAction state EmailAll = do
    res <- modalWidget (renderSigGroup state) $ emailAllForm state.account state.docId $
        map toEmailEntry $ filter (_.field.can.request) state.sigs
    case res of
        ModalClosed -> pure $ SigGroupChanged state
        ModalReturned requests -> pure $ SigGroupMutation state $ SignatureEmailRequests requests
  where
    toEmailEntry :: SigWidgetState -> EmailListEntry
    toEmailEntry { field, info: { email } } = { field, email, selected: false, doc: Nothing }


renderSigGroup :: SigGroupState -> Widget HTML (Either SigGroupResult SigGroupAction)
renderSigGroup state = D.div
    [ P.className "signature-group" ]
    $ sigWidgets <>
    [ actionBtns ]
  where
    sigWidgets = mapWithIndex sigWidget state.sigs
    sigWidget :: Int -> SigWidgetState -> Widget HTML (Either SigGroupResult SigGroupAction)
    sigWidget idx sig = do
        result <- signatureWidget sig
        case result of
            SigWidgetChanged sig' ->
                pure $ Left $ SigGroupChanged state { sigs = newSigs sig' }
            SigWidgetMutation sig' request ->
                pure $ Left $ SigGroupMutation (state { sigs = newSigs sig' }) $ SignatureWidgetRequest request
      where
        newSigs sig' = fromMaybe state.sigs $ Arr.updateAt idx sig' state.sigs
    
    actionBtns :: Widget HTML (Either SigGroupResult SigGroupAction)
    actionBtns = D.div [ P.className "btn-group btn-group-email" ] $ catMaybes
        [ justIf (any (_.field.can.request) state.sigs) $ D.button
            [ P.className "btn email-all", Right EmailAll <$ P.onClick ]
            [ D.text $ lookupTranslation "email-all" ]
        ]

--| Takes signature fields, authoritative signatures, and updates local state.
-- @todo: code smell alert!
-- This closely ties any module using it to this widget.
refreshFieldPermissions :: Array DocSigField -> SigGroupState -> SigGroupState
refreshFieldPermissions signatureFields state =
    state { sigs = map refreshSig state.sigs }
  where
    findKey :: SignKey -> Maybe DocSigField
    findKey key = find (\field -> field.key == key) signatureFields

    refreshSig sig = case findKey sig.field.key of
        Just field -> loadDefaults AuthoritativeOp (sig { field = normalizeDocSigField field }) field
        Nothing -> sig
